import React, { useEffect } from 'react';
import { Box, Text, Image, Tooltip } from '@chakra-ui/react';
import { generateColorScale } from '../utils/colorScale';

const hoverRevealSx = {
  '&:hover .reveal': {
    transform: 'translateY(0)',
    opacity: 1,
  },
};

const Reveal = ({ children }) => (
  <Box
    className="reveal"
    position="absolute"
    bottom={0}
    left={0}
    right={0}
    px={3}
    py={2}
    bg="blackAlpha.700"
    backdropFilter="blur(4px)"
    transform="translateY(100%)"
    opacity={0}
    transition="transform 0.2s ease, opacity 0.2s ease"
    pointerEvents="none"
  >
    {children}
  </Box>
);

const NameCard = ({ company_name, primaryScale, heading_font }) => (
  <Box
    bg={primaryScale?.[50] || 'gray.50'}
    borderRadius="lg"
    display="flex"
    alignItems="center"
    justifyContent="center"
    p={5}
    position="relative"
    overflow="hidden"
    sx={hoverRevealSx}
    gridArea="name"
  >
    <Text
      fontSize="xl"
      fontWeight="800"
      color={primaryScale?.[700] || 'gray.700'}
      fontFamily={heading_font ? `'${heading_font}', serif` : 'inherit'}
      letterSpacing="-0.02em"
      textAlign="center"
      noOfLines={2}
    >
      {company_name || '—'}
    </Text>
    <Reveal>
      <Text fontSize="10px" fontWeight="700" color="whiteAlpha.600" letterSpacing="0.1em" textTransform="uppercase">Brand name</Text>
    </Reveal>
  </Box>
);

const LogoCard = ({ logo_url, company_name, primaryScale }) => (
  <Box
    bg="white"
    border="1px solid"
    borderColor="gray.100"
    borderRadius="lg"
    display="flex"
    flexDir="column"
    alignItems="center"
    justifyContent="center"
    p={4}
    position="relative"
    overflow="hidden"
    sx={hoverRevealSx}
    gridArea="logo"
  >
    {logo_url ? (
      <Image
        src={logo_url}
        alt={company_name || 'Logo'}
        w="100%"
        h="100%"
        maxH="160px"
        objectFit="contain"
        onError={(e) => { e.target.style.display = 'none'; }}
      />
    ) : (
      <>
        <Box as="span" className="material-icons-outlined" fontSize="28px" color="gray.300">image</Box>
        <Text fontSize="10px" color="gray.300" fontWeight="500" mt={1}>Add logo</Text>
      </>
    )}
    <Reveal>
      <Text fontSize="10px" fontWeight="700" color="whiteAlpha.600" letterSpacing="0.1em" textTransform="uppercase">Logo</Text>
    </Reveal>
  </Box>
);

const ColorCard = ({ color, label, scale, gridArea }) => {
  if (!color) return (
    <Box
      bg="gray.100"
      borderRadius="lg"
      display="flex"
      alignItems="center"
      justifyContent="center"
      gridArea={gridArea}
    >
      <Text fontSize="xs" color="gray.300">—</Text>
    </Box>
  );

  return (
    <Box
      bg={color}
      borderRadius="lg"
      position="relative"
      overflow="hidden"
      sx={hoverRevealSx}
      gridArea={gridArea}
    >
      <Box position="absolute" bottom={0} left={0} right={0} display="flex" height="5px">
        {[100, 200, 300, 400, 500, 600, 700, 800].map(grade => (
          <Box key={grade} flex={1} bg={scale?.[grade] || color} />
        ))}
      </Box>
      <Reveal>
        <Text fontSize="10px" fontWeight="700" color="whiteAlpha.600" letterSpacing="0.1em" textTransform="uppercase">{label}</Text>
        <Text fontSize="sm" fontWeight="600" color="white" fontFamily="mono">{color.toUpperCase()}</Text>
      </Reveal>
    </Box>
  );
};

const FontCard = ({ font, label, stack, isSuggested, gridArea }) => (
  <Box
    bg="white"
    border="1px solid"
    borderColor="gray.100"
    borderRadius="lg"
    display="flex"
    flexDir="column"
    alignItems="flex-start"
    justifyContent="center"
    px={4}
    position="relative"
    overflow="hidden"
    sx={hoverRevealSx}
    gridArea={gridArea}
  >
    {font ? (
      <>
        <Text
          fontSize="3xl"
          fontWeight="700"
          color="gray.800"
          fontFamily={`'${font}', ${stack}`}
          lineHeight={1}
          mb="2px"
        >
          Aa
        </Text>
        <Text fontSize="10px" color="gray.400" fontFamily={`'${font}', ${stack}`} noOfLines={1}>
          The quick brown fox
        </Text>
        {isSuggested && (
          <Tooltip label="Closest Google Font match — exact font may differ" placement="top" fontSize="xs">
            <Box
              position="absolute"
              top={2}
              right={2}
              w="6px"
              h="6px"
              borderRadius="full"
              bg="orange.300"
            />
          </Tooltip>
        )}
      </>
    ) : (
      <Text fontSize="xs" color="gray.300">No font</Text>
    )}
    <Reveal>
      <Text fontSize="10px" fontWeight="700" color="whiteAlpha.600" letterSpacing="0.1em" textTransform="uppercase">{label}</Text>
      <Text fontSize="sm" fontWeight="600" color="white">{font || '—'}</Text>
      {isSuggested && <Text fontSize="10px" color="whiteAlpha.600">Closest match</Text>}
    </Reveal>
  </Box>
);

const BrandPreview = ({ brand }) => {
  const {
    company_name, logo_url,
    primary_color, secondary_color,
    page_bg_color, card_bg_color, heading_color, text_color,
    heading_font, body_font, font_confidence,
  } = brand;

  const primaryScale   = primary_color   ? generateColorScale(primary_color)   : null;
  const secondaryScale = secondary_color ? generateColorScale(secondary_color) : null;
  const pageBgScale    = page_bg_color   ? generateColorScale(page_bg_color)   : null;
  const cardBgScale    = card_bg_color   ? generateColorScale(card_bg_color)   : null;
  const headingScale   = heading_color   ? generateColorScale(heading_color)   : null;
  const textScale      = text_color      ? generateColorScale(text_color)      : null;
  const isSuggested    = font_confidence === 'suggested';

  useEffect(() => {
    const fonts = [heading_font, body_font].filter(Boolean);
    if (fonts.length === 0) return;
    const families = fonts.map(f => f.replace(/ /g, '+')).join('&family=');
    const link = document.createElement('link');
    link.id = 'brand-preview-fonts';
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${families}&display=swap`;
    document.head.appendChild(link);
    return () => document.getElementById('brand-preview-fonts')?.remove();
  }, [heading_font, body_font]);

  return (
    <Box
      display="grid"
      gridTemplateAreas={`
        "name     primary    secondary  pagebg  "
        "logo     headcol    textcol    cardbg  "
        "logo     headfont   bodyfont   bodyfont"
      `}
      gridTemplateColumns="2fr 1fr 1fr 1fr"
      gridTemplateRows="150px 130px 100px"
      gap="3px"
      borderRadius="xl"
      overflow="hidden"
      bg="gray.100"
    >
      <NameCard company_name={company_name} primaryScale={primaryScale} heading_font={heading_font} />
      <ColorCard color={primary_color}   label="Primary"    scale={primaryScale}   gridArea="primary" />
      <ColorCard color={secondary_color} label="Secondary"  scale={secondaryScale} gridArea="secondary" />
      <ColorCard color={page_bg_color}   label="Page bg"    scale={pageBgScale}    gridArea="pagebg" />

      <LogoCard logo_url={logo_url} company_name={company_name} primaryScale={primaryScale} />
      <ColorCard color={heading_color}   label="Heading"    scale={headingScale}   gridArea="headcol" />
      <ColorCard color={text_color}      label="Text"       scale={textScale}      gridArea="textcol" />
      <ColorCard color={card_bg_color}   label="Card bg"    scale={cardBgScale}    gridArea="cardbg" />

      <FontCard font={heading_font} label="Heading font" stack="serif"      isSuggested={isSuggested} gridArea="headfont" />
      <FontCard font={body_font}    label="Body font"    stack="sans-serif" isSuggested={isSuggested} gridArea="bodyfont" />
    </Box>
  );
};

export default BrandPreview;
